import { supabase } from '../../integrations/supabase/client';
import type { IBusinessUnitRepository } from '../interfaces/IBusinessUnitRepository';
import type { IBusinessUnitDb, ICreateBusinessUnitData, IUpdateBusinessUnitData, IUserBusinessUnitAssignment } from '../../interfaces/businessUnitDb.interface';

export class SupabaseBusinessUnitRepository implements IBusinessUnitRepository {
  async getAll(clientId: string): Promise<IBusinessUnitDb[]> {
    const { data, error } = await supabase
      .from('business_units')
      .select(`
        *,
        client:clients(id, name),
        manager:profiles!business_units_manager_user_id_fkey(id, full_name, email)
      `)
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as IBusinessUnitDb[];
  }

  async getById(id: string): Promise<IBusinessUnitDb | null> {
    const { data, error } = await supabase
      .from('business_units')
      .select(`
        *,
        client:clients(id, name),
        manager:profiles!business_units_manager_user_id_fkey(id, full_name, email)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as IBusinessUnitDb;
  }

  async create(data: ICreateBusinessUnitData): Promise<IBusinessUnitDb> {
    // Check for existing business unit with same name under same client
    const { data: existingUnits, error: checkError } = await supabase
      .from('business_units')
      .select('id, name')
      .eq('client_id', data.client_id)
      .ilike('name', data.name)
      .neq('status', 'deleted')
      .limit(1);

    if (checkError) {
      console.error('Error checking for duplicate business unit:', checkError);
    } else if (existingUnits && existingUnits.length > 0) {
      throw new Error(`A business unit named "${data.name}" already exists for this organization`);
    }

    const { data: created, error } = await supabase
      .from('business_units')
      .insert([data])
      .select()
      .single();

    if (error) {
      // Handle unique constraint violation with a user-friendly message
      if (error.code === '23505') {
        throw new Error(`A business unit named "${data.name}" already exists for this organization`);
      }
      throw error;
    }
    return created as IBusinessUnitDb;
  }

  async update(id: string, data: IUpdateBusinessUnitData): Promise<IBusinessUnitDb> {
    // If updating name, check for duplicates
    if (data.name) {
      // First get the current business unit to know its client_id
      const { data: currentUnit, error: fetchError } = await supabase
        .from('business_units')
        .select('client_id')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Check for existing business unit with same name under same client
      const { data: existingUnits, error: checkError } = await supabase
        .from('business_units')
        .select('id, name')
        .eq('client_id', currentUnit.client_id)
        .ilike('name', data.name)
        .neq('status', 'deleted')
        .neq('id', id)
        .limit(1);

      if (checkError) {
        console.error('Error checking for duplicate business unit:', checkError);
      } else if (existingUnits && existingUnits.length > 0) {
        throw new Error(`A business unit named "${data.name}" already exists for this organization`);
      }
    }

    const { data: updated, error } = await supabase
      .from('business_units')
      .update(data)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        throw new Error(`A business unit named "${data.name}" already exists for this organization`);
      }
      throw error;
    }
    return updated as IBusinessUnitDb;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('business_units')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async getUserBusinessUnits(userId: string): Promise<IBusinessUnitDb[]> {
    const { data, error } = await supabase
      .rpc('get_user_business_units', { user_id: userId });

    if (error) throw error;
    return (data || []) as unknown as IBusinessUnitDb[];
  }

  async assignUserToBusinessUnit(
    userId: string,
    businessUnitId: string,
    isPrimary: boolean,
    assignedBy?: string
  ): Promise<IUserBusinessUnitAssignment> {
    const { data, error } = await supabase
      .from('user_business_units')
      .insert({
        user_id: userId,
        business_unit_id: businessUnitId,
        is_primary: isPrimary,
        assigned_by: assignedBy
      })
      .select()
      .single();

    if (error) throw error;
    return data as IUserBusinessUnitAssignment;
  }

  async removeUserFromBusinessUnit(userId: string, businessUnitId: string): Promise<void> {
    const { error } = await supabase
      .from('user_business_units')
      .delete()
      .eq('user_id', userId)
      .eq('business_unit_id', businessUnitId);

    if (error) throw error;
  }

  async setPrimaryBusinessUnit(userId: string, businessUnitId: string): Promise<void> {
    // First, unset all primary flags for this user
    await supabase
      .from('user_business_units')
      .update({ is_primary: false })
      .eq('user_id', userId);

    // Then set the new primary
    const { error } = await supabase
      .from('user_business_units')
      .update({ is_primary: true })
      .eq('user_id', userId)
      .eq('business_unit_id', businessUnitId);

    if (error) throw error;
  }

  async getBusinessUnitStats(businessUnitId: string): Promise<{
    user_count: number;
    brand_count: number;
  }> {
    // Get user count
    const { count: userCount, error: userError } = await supabase
      .from('user_business_units')
      .select('*', { count: 'exact', head: true })
      .eq('business_unit_id', businessUnitId);

    if (userError) throw userError;

    // Brand count would come from brands table when implemented
    // For now, return 0
    return {
      user_count: userCount || 0,
      brand_count: 0
    };
  }
}
